import { takeLatest, put, call, select } from 'redux-saga/effects';
import { push } from 'connected-react-router';

import config, { LOGIN_URL } from 'js/config/config';
import { ROUTE_CODES } from 'js/config/routes';
import { refreshUser, cognitoSignOut, getCognitoUserId } from 'js/shared/helpers/authHelper';
import isElectron from 'js/shared/helpers/isElectron';

import {
  VALIDATE_TOKEN_REQUESTED,
  LOGOUT,
  logout as logoutAction,
  authSuccess,
  validateTokenFailure,
  validateTokenSuccess,
  setAccountManagementRedirect,
  setNoOnlineStorage
} from '../actionCreators/authActions';
import { reset } from '../actionCreators/generalActions';
import * as tokenHelper from '../shared/helpers/tokenHelpers';
import { appServices } from '../shared/helpers/app-services/AppServices';
import { sendErrorToSentry } from '../logging';

import redirectUserToReturnPath from './sagaHelpers/redirectUserToReturnPath';
import { validateAuthCookies } from './sagaHelpers/validateAuthCookies';
import { trackLogout } from './mixpanel';

const { MANAGE_ACCOUNT_URL } = config;

function* validateToken({ revalidation, userUpgraded }) {
  let authResponse;
  const pathname = yield select(({ router }) => router.location.pathname);

  const currentUserId = yield select(({ auth }) => auth.currentUser.id);

  try {
    authResponse = yield call(refreshUser);
    if (currentUserId !== '' && currentUserId !== authResponse.idToken.payload['custom:uid']) {
      if (isElectron()) {
        yield put(push(ROUTE_CODES.DESKTOP_LOGIN));
      } else {
        window.location.reload();
      }
    }
  } catch (error) {
    yield put(validateTokenFailure(error));
    if (pathname.includes('/render') || pathname.startsWith('/auth') || pathname.includes('/preview')) return;
    yield put(logoutAction({ userInitiated: false }));
    return;
  }

  let user;
  let userError = 'Unknown user error';
  let currentUser;
  const uidCookie = parseInt(tokenHelper.getUID(), 10);
  const sparkolToken = tokenHelper.getSparkolToken();

  try {
    currentUser = yield call(tokenHelper.getUserInfo);
  } catch (error) {
    console.warn('No user info stored in cookies', error);
    currentUser = { accountType: 'FREE' };
  }

  const currentCookiesAreValid = validateAuthCookies(sparkolToken, uidCookie, currentUser, uidCookie);

  try {
    if (currentCookiesAreValid && !userUpgraded) {
      user = currentUser;
    } else {
      user = yield call(
        { fn: appServices.exchangeCognitoTokenForSparkolToken, context: appServices },
        authResponse?.idToken?.jwtToken
      );
    }
  } catch (error) {
    if (error.errorCode === 'online_storage_disabled') {
      return yield put(setNoOnlineStorage(error));
    }

    userError = error;
    sendErrorToSentry(error);
    console.error(error);

    if (!pathname.includes('/render')) {
      yield put(logoutAction());
    }
  }

  if (!user) {
    yield put(validateTokenFailure(userError));
    return;
  }

  if ((!currentCookiesAreValid || userUpgraded) && user.token) {
    yield call(tokenHelper.setSparkolCookies, { token: user.token, tokenTtl: user.tokenTtl, user });
  }

  yield put(authSuccess(user, revalidation));
  if (!revalidation) {
    yield call(externalRedirect);
    yield redirectUserToReturnPath();
  }
  yield put(validateTokenSuccess(user));
}

function* externalRedirect() {
  const accountManagementRedirect = yield select(state => state.auth.accountManagementRedirect);

  if (accountManagementRedirect) {
    yield put(setAccountManagementRedirect(false));
    window.location.replace(`${MANAGE_ACCOUNT_URL}?referrer=loginredirect`);
    return yield;
  }
}

function* logout({ userInitiated }) {
  if (userInitiated) {
    yield call(trackLogout, userInitiated);
  }
  if (navigator.onLine) {
    yield call(appServices.deleteSession);
  }
  const cognitoUserId = yield call(getCognitoUserId);
  if (cognitoUserId) {
    if (navigator.onLine) {
      yield call(cognitoSignOut);
    } else {
      // Don't await signout call if user is offline
      cognitoSignOut();
    }
    yield call(tokenHelper.clearAuthLocalStorage, cognitoUserId, config.COGNITO_CLIENT_ID);
  }
  yield call(tokenHelper.removeSparkolCookies);
  yield put(reset());

  if (isElectron()) {
    window.electron?.user?.logOut();
    yield put(push(ROUTE_CODES.DESKTOP_LOGIN));
  } else {
    window.location.href = LOGIN_URL;
  }
}

function* authSagas() {
  yield takeLatest(VALIDATE_TOKEN_REQUESTED, validateToken);
  yield takeLatest(LOGOUT, logout);
}

export default authSagas;
